import { Grid } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import BlackSelectInput from 'app/views/components/inputs/BlackSelectInput'
import BlackTextInput from 'app/views/components/inputs/BlackTextInput'
import { formatNumberToCurrency } from 'app/utils/util-functions'
import IonIcon from '@reacticons/ionicons'
import { ReactComponent as ChevronDownIcon } from 'app/styles/chevronDown.svg'

interface ILendersArray {
    label: string
    value: string
    disabled: boolean
}

interface LenderPositionFieldProps {
    form: any;
    index: number;
    lendersArray: ILendersArray[];
    selectedOptions: string[];
    disableLenderPositions: boolean;
    amendment?: string | null;
    facility: any;
}

const LenderPositionField: React.FC<LenderPositionFieldProps> = ({
    form,
    index,
    lendersArray,
    selectedOptions,
    disableLenderPositions,
    amendment,
    facility,
}) => {
    const selectedOption = form.values.lendersPosition[index].lenders.id;
    const updatedOptions = lendersArray.map(option => ({
        ...option,
        disabled: selectedOptions.includes(option.value) && option.value !== selectedOption,
    }));

    const [inputValue, setInputValue] = useState(
        formatNumberToCurrency(form.values.lendersPosition[index].amount || 0, facility?.baseCurrency ?? 'USD')
    );
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        // Only auto-format if input is not currently focused (user not typing)
        if (!isFocused) {
            const formAmount = form.values.lendersPosition[index].amount;
            setInputValue(formatNumberToCurrency(formAmount || 0, facility?.baseCurrency ?? 'USD'));
        }
    }, [form.values.lendersPosition[index].amount, isFocused]);

    return (
        <Grid
            align="center"
            gutter={0}
            mb={0}
            className="lender-position-row"
        >
            <Grid.Col span="auto" className="lender-position-col-number">
                <div className="lender-allocation-row-number">
                    {index + 1}
                </div>
            </Grid.Col>
            <Grid.Col span={8} className="lender-position-col-lender">
                <BlackSelectInput
                    searchable
                    data={updatedOptions}
                    placeholder="Select Lender"
                    disabled={(disableLenderPositions || amendment) && !form.values.lendersPosition[index].newField}
                    {...form.getInputProps(`lendersPosition.${index}.lenders.id`)}
                    classNames={{
                        input: "lender-allocation-select",
                        rightSection: "lender-position-select-section"
                    }}
                    rightSection={<ChevronDownIcon style={{ color: '#9CA3AF', opacity: 0.5 }} />}
                />
            </Grid.Col>
            <Grid.Col span={3}>
                <BlackTextInput
                    placeholder="Enter amount"
                    disabled={disableLenderPositions}
                    value={inputValue}
                    onChange={(e) => {
                        const rawValue = e.target.value;
                        setInputValue(rawValue);
                        
                        const cleanValue = rawValue.replace(/[^0-9.]/g, '');
                        const numericValue = cleanValue === '' || cleanValue === '.' ? 0 : parseFloat(cleanValue) || 0;
                        form.setFieldValue(`lendersPosition.${index}.amount`, numericValue);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                        let cleanValue = inputValue.replace(/[^0-9.]/g, '');
                        const parts = cleanValue.split('.');

                        if (parts.length > 2) {
                            cleanValue = `${parts[0]}.${parts.slice(1).join('')}`;
                        }

                        const [integerPart, decimalPart] = cleanValue.split('.');

                        if (decimalPart && decimalPart.length > 2) {
                            cleanValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
                        }

                        const numericValue = cleanValue === '' || cleanValue === '.' ? 0 : parseFloat(cleanValue) || 0;
                        form.setFieldValue(`lendersPosition.${index}.amount`, numericValue);

                        setInputValue(formatNumberToCurrency(numericValue, facility?.baseCurrency ?? 'USD'));
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        const currentValue = form.values.lendersPosition[index].amount;
                        setInputValue(currentValue === 0 ? '' : currentValue.toString());
                    }}
                    classNames={{
                        input: "lender-allocation-text-input lender-position-amount-input"
                    }}
                />
            </Grid.Col>
            <Grid.Col span="auto" className="lender-position-col-close">
                {!disableLenderPositions && (
                    <div className="lender-position-close-button-container">
                        <IonIcon
                            name="close"
                            className="click-ion-icon lender-position-close-button"
                            onClick={() => form.removeListItem('lendersPosition', index)}
                        />
                    </div>
                )}
            </Grid.Col>
        </Grid>
    );
};

export default LenderPositionField;